import React, { useState, useEffect } from 'react';
import { Save, Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getEmailSettings, updateEmailSettings } from '@/services/settingsApi';

export const EmailSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [smtpProvider, setSmtpProvider] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [encryption, setEncryption] = useState('TLS');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [transactionalEmail, setTransactionalEmail] = useState(true);
  const [marketingEmail, setMarketingEmail] = useState(true);
  const [reviewNotifications, setReviewNotifications] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getEmailSettings();
        const data = res?.data || res || {};
        setSmtpProvider(data.smtp_provider || '');
        setSmtpHost(data.smtp_host || '');
        setSmtpPort(data.smtp_port ?? '');
        setEncryption(data.encryption || 'TLS');
        setSmtpUsername(data.smtp_username || '');
        setSmtpPassword(data.smtp_password || '');
        setFromEmail(data.from_email || '');
        setFromName(data.from_name || '');
        setReplyToEmail(data.reply_to_email || '');
        setTransactionalEmail(data.transactional_email ?? true);
        setMarketingEmail(data.marketing_email ?? true);
        setReviewNotifications(data.review_notifications ?? true);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load email settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateEmailSettings({
        smtp_provider: smtpProvider,
        smtp_host: smtpHost,
        smtp_port: Number(smtpPort) || 0,
        encryption,
        smtp_username: smtpUsername,
        smtp_password: smtpPassword,
        from_email: fromEmail,
        from_name: fromName,
        reply_to_email: replyToEmail,
        transactional_email: transactionalEmail,
        marketing_email: marketingEmail,
        review_notifications: reviewNotifications,
      });
      toast.success('Email settings saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">SMTP Configuration</h3>
          <div className="space-y-4">
            <div>
              <Label>SMTP Provider</Label>
              <Input
                value={smtpProvider}
                onChange={(e) => setSmtpProvider(e.target.value)}
                placeholder="e.g., SendGrid, Mailgun, AWS SES"
                className="mt-2"
              />
            </div>
            <div>
              <Label>SMTP Host</Label>
              <Input
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="smtp.example.com"
                className="mt-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SMTP Port</Label>
                <Input
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  placeholder="587"
                  type="number"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Encryption</Label>
                <select
                  value={encryption}
                  onChange={(e) => setEncryption(e.target.value)}
                  className="mt-2 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="TLS">TLS</option>
                  <option value="SSL">SSL</option>
                  <option value="None">None</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SMTP Username</Label>
                <Input
                  value={smtpUsername}
                  onChange={(e) => setSmtpUsername(e.target.value)}
                  placeholder="your-username"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>SMTP Password</Label>
                <Input
                  type="password"
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <h3 className="font-semibold text-slate-900 mb-3">Email Addresses</h3>
          <div className="space-y-4">
            <div>
              <Label>From Email</Label>
              <Input
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>From Name</Label>
              <Input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Reply-To Email</Label>
              <Input
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <h3 className="font-semibold text-slate-900 mb-3">Email Types</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-900">Transactional Emails</p>
                <p className="text-xs text-slate-500">Welcome, password reset, notifications</p>
              </div>
              <Switch checked={transactionalEmail} onCheckedChange={setTransactionalEmail} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-900">Marketing Emails</p>
                <p className="text-xs text-slate-500">Newsletters, promotional content</p>
              </div>
              <Switch checked={marketingEmail} onCheckedChange={setMarketingEmail} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-900">Review Notifications</p>
                <p className="text-xs text-slate-500">New review alerts for admins</p>
              </div>
              <Switch checked={reviewNotifications} onCheckedChange={setReviewNotifications} />
            </div>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
};